import { IonPhaser } from '@ion-phaser/react';
import { Button, Layout, Modal, Result, Space, Spin, Typography } from 'antd';
import { ethers } from 'ethers';
import Phaser from 'phaser';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import OriginLeaderboardContractJson from "../../contracts/OriginLeaderboard.json";
import { Game as GameScene } from "../../game/scenes/Game";
import { GameOver as GameOverScene } from "../../game/scenes/GameOver";
import { getEventEmitter } from '../../game/services/EventService';
import { getActiveToken, setActiveToken } from "../../game/services/TokenService";
import { Footer } from "../components/Footer";
import { Navigation } from "../components/Navigation";
import { GAME_VERSION, ORIGIN_LEADERBOARD_CONTRACT_ADDRESS } from "../Constants";
import * as analytics from "../services/Analytics";
import "./Game.css";

export function Game() {

    const location = useLocation();
    const navigate = useNavigate(); 

    const [isLoading, setIsLoading] = useState(true);

    const [isGameStarted, setIsGameStarted] = useState(false);

    /** @type {import('@ion-phaser/react').GameInstance} */
    const [game, setGame] = useState(null);

    const [handledPlayerScore, setHandledPlayerScore] = useState(null);

    /**@type {React.RefObject<HTMLIonPhaserElement>} */
    const gameRef = useRef(null);

    /** @type {[import('react').Contract, Function]} */
    const [originLeaderboardContract, setOriginLeaderboardContract] = useState(null);

    const gameConfig = {
        type: Phaser.AUTO,
        backgroundColor: '#141414',
        scale: {
            parent: "game-container",
            mode: Phaser.Scale.FIT,
            autoCenter: Phaser.Scale.CENTER_BOTH,
            width: 800,
            height: 1200
        },
        physics: {
            default: 'arcade',
            arcade: {
                gravity: { y: 1800 },
                debug: false
            }
        },
        scene: [GameScene, GameOverScene]
    }

    function startGame() {

        analytics.clickStartGame();

        setGame(gameConfig);
        setIsGameStarted(true);
        getEventEmitter().on("savePlayerScore", handleSavePlayerScore);
    }

    function closeGame() {
        gameRef.current.destroy();
        setIsGameStarted(false);
        setGame(undefined);
        getEventEmitter().off("savePlayerScore", handleSavePlayerScore);
    }

    function handleSavePlayerScore(playerScore) {
        setHandledPlayerScore(playerScore);
    }

    async function savePlayerScore() {
        try {

            setIsLoading(true);

            analytics.clickSavePlayerScore();

            await originLeaderboardContract.addRecord(GAME_VERSION, getActiveToken().edition, handledPlayerScore);

            Modal.info({
                title: "Congratulations 🎉",
                content: `Your score "${handledPlayerScore}" will be on the leaderboard soon. Provided that the transaction was successful.`,
            });

        } catch (error) {
            console.log(error);
            analytics.getError("Failed to save player score: " + JSON.stringify(error));
            Modal.error({
                title: "Ooops...",
                content: "Failed to save your score",
            });
        } finally {
            setIsLoading(false);
        }
    }

    function renderContent() {

        if (isLoading) {
            return (
                <div className="layout-content-element"></div>
            )
        }
        else if (!isGameStarted) {
            return (
                <div className="layout-content-element">
                    <Result
                        icon={<div />}
                        title={<Typography.Title level={1}>🚀 Origin Game</Typography.Title>}
                        subTitle={
                            <Space direction='vertical'>
                                <Typography.Paragraph>Your prisoner is on the run. The space police are chasing you.</Typography.Paragraph>
                                <Typography.Paragraph strong>🎯 Try to run as far as possible.</Typography.Paragraph>
                                <Typography.Paragraph>Your score will determine what benefits you will receive in the Crypto Space Prison.</Typography.Paragraph>
                                <Typography.Paragraph strong>🎮 Use the ARROW KEYS or SCREEN BUTTONS to control your prisoner.</Typography.Paragraph>
                            </Space>
                        }
                        extra={<Button type='primary' size='large' onClick={() => startGame()}>Start game</Button>}
                    />
                </div>
            )
        }
        else {
            return (
                <div className="game-container" id="game-container">
                    <IonPhaser ref={gameRef} game={game} initialize={isGameStarted} />
                </div>
            )
        }
    }

    useEffect(() => {
        analytics.openGamePage();
    }, []);

    useEffect(() => {

        // Check wallet
        if (!window.ethereum) {
            console.log('Provider is not exists');
            navigate("/dashboard");
            return;
        }

        // Check token
        if (!location.state || !location.state.token) {
            console.log("Location state is incorrect");
            navigate("/dashboard");
            return;
        }

        // Save token
        setActiveToken(location.state.token);

        // Init leaderboard contract
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const signer = provider.getSigner();
        const contract = new ethers.Contract(
            ORIGIN_LEADERBOARD_CONTRACT_ADDRESS,
            OriginLeaderboardContractJson.abi,
            signer
        );
        setOriginLeaderboardContract(contract);

        setIsLoading(false);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (handledPlayerScore !== null) {
            closeGame();
            savePlayerScore();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handledPlayerScore])

    return (
        <Layout>
            <Navigation />
            <Layout.Content className="layout-content" >
                <Spin spinning={isLoading}>
                    {renderContent()}
                </Spin>
            </Layout.Content>
            <Footer />
        </Layout >
    )
}